import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MessageCircle, ShieldCheck, RefreshCw, RotateCcw } from "lucide-react";

const TEMPLATE_LABELS = {
  TWILIO_TEMPLATE_ORDER_RECEIVED: "Order received",
  TWILIO_TEMPLATE_ORDER_CONFIRMED: "Order confirmed",
  TWILIO_TEMPLATE_READY_FOR_PICKUP: "Ready for pickup",
  TWILIO_TEMPLATE_OUT_FOR_DELIVERY: "Out for delivery",
  TWILIO_TEMPLATE_DELIVERED: "Delivered",
  TWILIO_TEMPLATE_CANCELLED: "Cancelled",
};

// Production order-lifecycle templates (source of truth: user-provided ContentSids)
const ORDER_TEMPLATES = [
  { name: "ordersforbuyer", role: "Buyer", envKey: "TWILIO_TEMPLATE_ORDER_BUYER", desc: "Sent to the customer after a successful order." },
  { name: "ordersforsellers", role: "Seller", envKey: "TWILIO_TEMPLATE_ORDER_SELLER", desc: "Sent to each seller for their portion of the order." },
  { name: "ordersforadmin", role: "Admin", envKey: "TWILIO_TEMPLATE_ORDER_ADMIN", desc: "Sent to +592 666 5489 for every order." },
];

function prettySender(raw) {
  if (!raw) return "";
  const digits = raw.replace(/^whatsapp:/, "").replace(/[^\d+]/g, "");
  if (digits.startsWith("+5926") && digits.length === 11) {
    return `+592 ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits === "+14155238886") return "+1 (415) 523 8886";
  return digits;
}

function StatusPill({ status }) {
  const map = {
    sent: "text-white bg-[#00A355] border-[#00A355]",
    pending: "text-[#FF9E00] border-[#FF9E00]",
    failed: "text-white bg-[#FF2A00] border-[#FF2A00]",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${map[status] || "text-neutral-500 border-neutral-300"}`}>
      {status}
    </span>
  );
}

export default function AdminWhatsApp() {
  const [config, setConfig] = useState(null);
  const [mode, setMode] = useState("template");
  const [to, setTo] = useState("");
  const [body, setBody] = useState("Hello from Guyana Discounts!");
  const [contentSid, setContentSid] = useState("");
  const [variablesText, setVariablesText] = useState('{"1": "Alice", "2": "Order #ABC123"}');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [raffleMsg, setRaffleMsg] = useState("");
  const [raffleMsgSaving, setRaffleMsgSaving] = useState(false);

  const loadConfig = useCallback(() => {
    api.get("/admin/whatsapp/config")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ configured: false, sender: null, mode: "sandbox", templates: {} }));
    api.get("/settings")
      .then((r) => setRaffleMsg(r.data.raffle_message || ""))
      .catch(() => {});
  }, []);

  const saveRaffleMsg = async () => {
    setRaffleMsgSaving(true);
    try {
      await api.put("/admin/settings", { raffle_message: raffleMsg.trim() || null });
      toast.success("Raffle message saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setRaffleMsgSaving(false);
    }
  };

  const loadNotifs = useCallback(async () => {
    setNotifsLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/notifications?limit=100");
      setNotifs(data.items || []);
    } catch {
      /* ignore */
    }
    setNotifsLoading(false);
  }, []);

  useEffect(() => { loadConfig(); loadNotifs(); }, [loadConfig, loadNotifs]);

  const isProduction = config?.mode === "production";
  const templates = config?.templates || {};
  const configuredTemplates = Object.entries(templates).filter(([, v]) => !!v);

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a WhatsApp phone number"); return; }
    const payload = { to: to.trim() };

    if (mode === "template") {
      if (!contentSid.trim().startsWith("H")) {
        toast.error("Enter a valid Twilio Content SID (starts with H)");
        return;
      }
      payload.content_sid = contentSid.trim();
      if (variablesText.trim()) {
        try {
          payload.content_variables = JSON.parse(variablesText);
        } catch {
          toast.error("Variables must be valid JSON, e.g. {\"1\":\"Alice\"}");
          return;
        }
      }
    } else {
      if (!body.trim()) { toast.error("Enter a message body"); return; }
      payload.body = body;
    }

    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/whatsapp/test", payload);
      setResult(data);
      if (data.ok) toast.success("WhatsApp message queued");
      else toast.error(data?.error?.message || "Send failed");
    } catch (e) {
      const errPayload = e?.response?.data?.detail || e?.message || "Network error";
      setResult({ ok: false, error: { message: String(errPayload) } });
      toast.error("Send failed");
    }
    setSending(false);
  };

  const retry = async (n) => {
    setRetrying((s) => ({ ...s, [n.notification_id]: true }));
    try {
      await api.post(`/admin/whatsapp/notifications/${n.notification_id}/retry`);
      toast.success("Retry queued");
      setTimeout(loadNotifs, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying((s) => ({ ...s, [n.notification_id]: false }));
    }
  };

  return (
    <div data-testid="admin-whatsapp-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 bg-[#25D366] text-white grid place-items-center">
          <MessageCircle className="w-5 h-5" />
        </div>
        <h1 className="font-display font-black text-4xl tracking-tight">WhatsApp</h1>
        {config && (
          <>
            <span
              data-testid="whatsapp-config-status"
              className={`text-[11px] uppercase tracking-widest font-bold px-2 py-0.5 border ${
                config.configured
                  ? "text-[#00A355] border-[#00A355]"
                  : "text-[#FF2A00] border-[#FF2A00]"
              }`}
            >
              {config.configured ? "Configured" : "Not configured"}
            </span>
            <span
              data-testid="whatsapp-mode-badge"
              className={`text-[11px] uppercase tracking-widest font-bold px-2 py-0.5 border inline-flex items-center gap-1 ${
                isProduction
                  ? "text-white bg-[#00A355] border-[#00A355]"
                  : "text-[#FF9E00] border-[#FF9E00]"
              }`}
            >
              {isProduction && <ShieldCheck className="w-3 h-3" />}
              {config.mode}
            </span>
          </>
        )}
      </div>

      {config && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-3xl" data-testid="whatsapp-sender-panel">
          <div className="border border-black p-4 bg-[#F7F7F7]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
              WhatsApp {isProduction ? "Production" : "Sandbox"} Sender
            </div>
            <div className="mt-1 font-display font-black text-2xl">
              {prettySender(config.sender)}
            </div>
          </div>
          <div className="border border-black p-4 bg-[#F7F7F7]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
              Admin notification recipient
            </div>
            <div className="mt-1 font-display font-black text-2xl">
              +592 666 5489
            </div>
          </div>
        </div>
      )}

      {/* Approved order templates (source of truth for auto-notify) */}
      <div className="mt-6 border border-black p-4 max-w-3xl" data-testid="order-templates">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
          Approved order templates
        </div>
        <div className="space-y-2">
          {ORDER_TEMPLATES.map((t) => {
            const sid = templates[t.envKey];
            return (
              <div key={t.name} className="flex items-start justify-between gap-3 py-2 border-t border-neutral-200 first:border-t-0">
                <div>
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-[11px] text-neutral-500">{t.role} · {t.desc}</div>
                </div>
                {sid ? (
                  <code className="text-[10px] text-[#00A355] font-mono truncate max-w-[220px]" title={sid}>{sid}</code>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-[#FF2A00] font-bold">Missing</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Raffle contact message — used by the "Send WhatsApp" button on /admin raffle tiles */}
      <div className="mt-6 border border-black p-4 max-w-3xl" data-testid="raffle-message-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
          Raffle entry message
        </div>
        <p className="text-xs text-neutral-600 mb-2">
          Prewritten message used when you tap a raffle entry's name on the <span className="font-semibold">/admin</span> raffle
          tile and hit <span className="font-semibold">Send WhatsApp</span>. It opens your own WhatsApp with this text filled in.
        </p>
        <textarea
          data-testid="raffle-message-input"
          value={raffleMsg}
          onChange={(e) => setRaffleMsg(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder={"Hi {name}! Thanks for entering the {code} raffle on Guyana Discounts — you're entry #{position} for the {product}."}
          className="w-full border border-black rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
        />
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          <div className="text-[11px] text-neutral-500">
            Placeholders: <code className="font-mono">{"{name}"}</code> <code className="font-mono">{"{code}"}</code> <code className="font-mono">{"{product}"}</code> <code className="font-mono">{"{position}"}</code>
          </div>
          <Button
            data-testid="raffle-message-save"
            onClick={saveRaffleMsg}
            disabled={raffleMsgSaving}
            className="rounded-none bg-black text-white h-10 px-5 font-bold uppercase tracking-wider hover:bg-neutral-900 disabled:opacity-50"
          >
            {raffleMsgSaving ? "Saving…" : "Save message"}
          </Button>
        </div>
      </div>

      {/* Notification log */}
      <div className="mt-8 max-w-6xl" data-testid="notification-log">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-2xl">Recent WhatsApp activity</h2>
          <button
            data-testid="refresh-notif-log"
            onClick={loadNotifs}
            className="inline-flex items-center gap-2 h-9 px-3 border border-black text-xs font-semibold hover:bg-black hover:text-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${notifsLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="border border-black overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">When</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Order</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Role</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Recipient</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Template</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Status</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Msg SID</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Error</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold"></th>
              </tr>
            </thead>
            <tbody data-testid="notif-tbody">
              {notifs.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-500 text-sm">No WhatsApp activity yet.</td></tr>
              )}
              {notifs.map((n) => (
                <tr key={n.notification_id} className="border-t border-neutral-200 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-neutral-500">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.order_number}</td>
                  <td className="px-3 py-2 text-xs uppercase">{n.recipient_role}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.recipient_phone}</td>
                  <td className="px-3 py-2 text-xs">{n.template_name}</td>
                  <td className="px-3 py-2"><StatusPill status={n.status} /></td>
                  <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[140px]" title={n.message_sid || ""}>{n.message_sid || "—"}</td>
                  <td className="px-3 py-2 text-xs text-[#FF2A00] max-w-[220px] break-words">
                    {n.error_code ? <><span className="font-mono">{n.error_code}</span> · {n.error_message}</> : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {n.status === "failed" && (
                      <button
                        data-testid={`retry-${n.notification_id}`}
                        onClick={() => retry(n)}
                        disabled={!!retrying[n.notification_id]}
                        className="inline-flex items-center gap-1 h-8 px-2 border border-black text-[11px] font-semibold hover:bg-black hover:text-white disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" /> {retrying[n.notification_id] ? "…" : "Retry"}
                      </button>
                    )}
                    {n.retry_count > 0 && (
                      <span className="ml-1 text-[10px] text-neutral-500">×{n.retry_count}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ad-hoc test / debugging */}
      <div className="mt-10 max-w-2xl">
        <h2 className="font-display font-black text-2xl mb-2">Ad-hoc test</h2>
        <p className="text-sm text-neutral-600 mb-3">
          Manually fire a message. Auto-notifications on order placement run separately and are logged above.
        </p>

        <div className="inline-flex border border-black" data-testid="whatsapp-mode-toggle">
          <button
            data-testid="mode-template-btn"
            onClick={() => setMode("template")}
            className={`h-10 px-5 text-xs font-bold uppercase tracking-widest ${
              mode === "template" ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
            }`}
          >
            Content Template
          </button>
          <button
            data-testid="mode-freeform-btn"
            onClick={() => setMode("freeform")}
            className={`h-10 px-5 text-xs font-bold uppercase tracking-widest border-l border-black ${
              mode === "freeform" ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
            }`}
          >
            Free-form (24h window only)
          </button>
        </div>

        <div className="mt-4 border border-black p-6 bg-white">
          <div className="space-y-4">
            <div>
              <Label>Destination WhatsApp number</Label>
              <Input
                data-testid="whatsapp-test-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="+5926001234 or 6001234"
                className="rounded-none border-black h-12"
              />
            </div>

            {mode === "template" ? (
              <>
                <div>
                  <Label>Twilio Content SID (starts with H)</Label>
                  <Input
                    data-testid="whatsapp-content-sid"
                    value={contentSid}
                    onChange={(e) => setContentSid(e.target.value)}
                    placeholder="HXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                    className="rounded-none border-black h-12 font-mono"
                  />
                  {configuredTemplates.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold self-center mr-1">Pick:</span>
                      {configuredTemplates.map(([k, v]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setContentSid(v)}
                          className="text-[11px] px-2 py-1 border border-neutral-300 hover:border-black hover:bg-neutral-50"
                          data-testid={`template-pick-${k.toLowerCase()}`}
                        >
                          {k.replace("TWILIO_TEMPLATE_ORDER_", "").toLowerCase()}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Variables (JSON — positional keys)</Label>
                  <textarea
                    data-testid="whatsapp-content-vars"
                    value={variablesText}
                    onChange={(e) => setVariablesText(e.target.value)}
                    rows={3}
                    className="w-full border border-black rounded-none px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
                  />
                </div>
              </>
            ) : (
              <div>
                <Label>Free-form message</Label>
                <textarea
                  data-testid="whatsapp-test-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={3}
                  maxLength={1600}
                  className="w-full border border-black rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
                />
                {isProduction && (
                  <p className="text-[11px] text-[#FF9E00] mt-1 font-semibold">
                    ⚠︎ Free-form only reaches recipients who messaged you in the last 24 hours.
                  </p>
                )}
              </div>
            )}

            <Button
              data-testid="whatsapp-send-btn"
              onClick={send}
              disabled={sending || !config?.configured}
              className="rounded-none bg-black text-white h-12 px-6 font-bold uppercase tracking-wider hover:bg-neutral-900 disabled:opacity-50"
            >
              {sending ? "Sending…" : mode === "template" ? "Send Template" : "Send Free-form"}
            </Button>
          </div>

          {result && (
            <div
              data-testid="whatsapp-test-result"
              className={`mt-6 p-4 border-2 ${
                result.ok ? "border-[#00A355] bg-[#00A355]/5" : "border-[#FF2A00] bg-[#FF2A00]/5"
              }`}
            >
              <div className="font-display font-black uppercase tracking-wider text-lg mb-2">
                {result.ok ? "Success" : "Failed"}
              </div>
              {result.ok ? (
                <dl className="text-sm space-y-1">
                  <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Sender</dt><dd className="font-mono break-all">{config?.sender}</dd></div>
                  <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Destination</dt><dd className="font-mono break-all">{result.to}</dd></div>
                  <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Message SID</dt><dd className="font-mono break-all">{result.sid}</dd></div>
                  <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Status</dt><dd className="font-mono">{result.status}</dd></div>
                  {result.content_sid && (
                    <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Content SID</dt><dd className="font-mono break-all">{result.content_sid}</dd></div>
                  )}
                </dl>
              ) : (
                <dl className="text-sm space-y-1">
                  {result.error?.code !== undefined && (
                    <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Error code</dt><dd className="font-mono">{String(result.error.code)}</dd></div>
                  )}
                  {result.error?.http_status && (
                    <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">HTTP status</dt><dd className="font-mono">{result.error.http_status}</dd></div>
                  )}
                  <div className="flex gap-2"><dt className="text-neutral-500 min-w-[110px]">Description</dt><dd className="break-all">{result.error?.message}</dd></div>
                </dl>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
